import { Controller } from "@hotwired/stimulus"

// Kept in step with TerritoryCheck::UNAVAILABLE_RESULTS. `pending` is not one of them: the brand
// is still confirming, so asking for a reason would be premature.
const UNAVAILABLE_RESULTS = ["not_available", "not_viable"]

// Nearby entry controls and feedback validation, scoped to the response form and each card.
export default class extends Controller {
  static targets = ["footer", "progress", "next", "saveStatus", "confirmation", "confirmationHeading", "primary", "submitWithoutMaps"]
  static values = { submitted: Boolean, saveFailed: Boolean }

  /** @returns {void} Preserve the initial answers and observe the existing footer. */
  connect() {
    this.nextEntryIndex = Date.now()
    this.attempted = this.saveFailedValue
    this.preflightSubmit = (event) => this.handlePreflightSubmit(event)
    window.addEventListener("submit", this.preflightSubmit, true)
    this.syncNearbySections()
    this.element.dataset.initialAnswers ||= this.answers(this.element)
    this.cards().forEach((card) => { card.dataset.initialAnswers ||= this.answers(card) })
    this.resizeObserver = new ResizeObserver(() => this.resizeFooter())
    this.resizeObserver.observe(this.footerTarget)
    this.refresh()
    queueMicrotask(() => { if (this.element.isConnected) this.refresh() })
  }

  /** @returns {void} Release the size observer on navigation. */
  disconnect() {
    window.removeEventListener("submit", this.preflightSubmit, true)
    this.resizeObserver.disconnect()
  }

  /** @returns {HTMLElement[]} Requested location cards, excluding volunteered territories. */
  cards() { return Array.from(this.element.querySelectorAll(".territory-response-card-js")) }

  /**
   * @param {HTMLElement} container Form or location card.
   * @returns {string} Comparable answers, including selected files and nearby entries.
   */
  answers(container) {
    return JSON.stringify(Array.from(container.querySelectorAll("input, select, textarea"))
      .filter((input) => !["submit", "button"].includes(input.type))
      .map((input) => input.type === "file" ?
        [input.name, Array.from(input.files).map((file) => [file.name, file.size, file.lastModified])] :
        [input.name, input.value, input.type === "checkbox" ? input.checked : null, input.disabled]))
  }

  /** @returns {HTMLElement[]} Controls that prevent submission under existing rules. */
  invalidFields() {
    const invalid = Array.from(this.element.elements).filter((input) => input.willValidate && !input.validity.valid)
    const places = this.application.getControllerForElementAndIdentifier(this.element, "place-autocomplete-validation")
    invalid.push(...(places?.offenders() || []))
    this.cards().forEach((card) => {
      if (!this.cardValid(card)) invalid.push(card.querySelector(".alternate-notes--js"))
    })
    return [...new Set(invalid)].filter(Boolean)
  }

  /** @returns {void} Update completeness separately from saved and edited state. */
  refresh() {
    this.syncNearbySections()
    const invalid = this.invalidFields()
    const cards = this.cards()
    Array.from(this.element.elements).forEach((input) => {
      const showError = this.attempted && invalid.includes(input)
      input.classList.toggle("is-invalid", showError)
      if (showError) input.setAttribute("aria-invalid", "true")
      else input.removeAttribute("aria-invalid")
    })
    let readyCount = 0
    cards.forEach((card) => {
      const ready = !invalid.some((input) => card.contains(input))
      if (ready) readyCount++
      const changed = this.answers(card) !== card.dataset.initialAnswers || this.saveFailedValue
      const state = changed && this.submittedValue ? "changed" :
        (this.submittedValue && !changed ? "submitted" : (ready ? "ready" : "pending"))
      card.dataset.responseState = state
      const indicator = card.querySelector("[data-card-indicator]")
      const confirmed = ready && state !== "changed" && !this.saveFailedValue
      indicator.hidden = false
      indicator.classList.toggle("fa-square-check", confirmed)
      indicator.classList.toggle("fa-circle-info", !confirmed)
      indicator.dataset.complete = String(confirmed)
      const text = state === "changed" ? `Unsaved changes · ${ready ? "Ready to submit" : "Needs answers"}` :
        (state === "submitted" ? `Submitted${ready ? "" : " · Needs more detail"}` :
          (ready ? "Ready to submit" : "Needs answers"))
      this.setText(card.querySelector("[data-card-status]"), text)
    })
    const registrationNeeded = invalid.some((input) => !input.closest(".territory-response-card-js"))
    // The submit button stays enabled on purpose. Disabling it left the brand with a card reading
    // "Needs answers" and no way to find out which: errors surface on a submit attempt, so a
    // button that cannot be pressed is a button that never explains itself.
    this.setText(this.progressTarget, `${readyCount} of ${cards.length} locations ready${registrationNeeded ? " · Registration needed" : ""}`)
    this.nextTarget.hidden = invalid.length === 0
    this.setText(this.nextTarget, registrationNeeded ? "Answer registration" : "Next incomplete location")
    const changed = this.answers(this.element) !== this.element.dataset.initialAnswers || this.saveFailedValue
    this.setText(this.saveStatusTarget, changed ? "Unsaved changes" : (this.submittedValue ? "Response submitted" : ""))
  }

  /** @returns {void} Exclude inactive nearby fields while retaining explicit saved-row removals. */
  syncNearbySections() {
    this.cards().forEach((card) => {
      const active = card.querySelector(".alternate-nearby--js")?.value === "true"
      const section = card.querySelector(".nearby-section-js")
      section?.classList.toggle("d-none", !active)
      section?.querySelectorAll(".nearby-entry-js").forEach((entry) => {
        const removed = entry.querySelector(".nearby-entry-destroy-js")?.value === "true"
        entry.querySelectorAll("input, select, textarea, button").forEach((input) => {
          const removalField = removed && input.matches('input[name$="[id]"], .nearby-entry-destroy-js')
          input.disabled = !removalField && (!active || removed)
        })
      })
    })
  }

  /**
   * @param {HTMLElement} element Status element.
   * @param {string} text New message.
   * @returns {void} Avoid repeated screen-reader announcements on every keystroke.
   */
  setText(element, text) { if (element.textContent !== text) element.textContent = text }

  /** @returns {void} Show validation feedback only after a submit attempt. */
  invalid() {
    this.attempted = true
    this.refresh()
    queueMicrotask(() => { const input = this.invalidFields()[0]; if (input) this.focusField(input) })
  }

  /**
   * @param {SubmitEvent} event Attempted form submission.
   * @returns {void} Keep invalid submissions on the form and focus the missing answer.
   */
  validate(event) {
    this.attempted = true
    this.refresh()
    const invalid = this.invalidFields()[0]
    if (!invalid) return
    event.preventDefault()
    event.stopImmediatePropagation()
    this.focusField(invalid)
  }

  /**
   * @param {SubmitEvent} event Form submission captured before Active Storage.
   * @returns {void} Run response validation and map confirmation before uploads begin.
   */
  handlePreflightSubmit(event) {
    if (event.target !== this.element) return

    this.validate(event)
    if (!event.defaultPrevented) this.confirm(event)
  }

  /**
   * @param {SubmitEvent} event Validated submission attempt.
   * @returns {void} Offer attachments before sending an initial response without maps.
   */
  confirm(event) {
    if (event.defaultPrevented || !this.missingMaps().length) return
    if (!this.confirmationTarget.classList.contains("d-none") && event.submitter === this.submitWithoutMapsTarget) return
    event.preventDefault()
    event.stopImmediatePropagation()
    this.primaryTarget.classList.add("d-none")
    this.confirmationTarget.classList.remove("d-none")
    this.resizeFooter()
    this.confirmationHeadingTarget.focus({ preventScroll: true })
  }

  /** @returns {HTMLInputElement[]} Initial-response locations still missing attachments. */
  missingMaps() {
    return Array.from(this.element.querySelectorAll("input.require-image:not(.has-image-already)"))
      .filter((input) => !input.files.length)
  }

  /** @returns {void} Return to the form and focus the first upload needing a map. */
  addMaps() {
    this.confirmationTarget.classList.add("d-none")
    this.primaryTarget.classList.remove("d-none")
    this.resizeFooter()
    const upload = this.missingMaps()[0]
    if (upload) this.focusField(upload)
    else this.primaryTarget.querySelector("input[type='submit']").focus()
  }

  /** @returns {void} Move to registration or the next incomplete location. */
  nextIncomplete() {
    const invalid = this.invalidFields()
    const registration = invalid.find((input) => !input.closest(".territory-response-card-js"))
    if (registration) return this.focusField(registration)
    const cards = this.cards()
    const start = cards.indexOf(this.lastIncompleteCard) + 1
    const ordered = [...cards.slice(start), ...cards.slice(0, start)]
    const card = ordered.find((candidate) => invalid.some((input) => candidate.contains(input)))
    if (!card) return
    this.lastIncompleteCard = card
    this.focusField(invalid.find((input) => card.contains(input)))
  }

  /**
   * @param {HTMLElement} input Control needing attention.
   * @returns {void} Reveal and focus it above the sticky footer.
   */
  focusField(input) {
    input.scrollIntoView({ block: "center", behavior: "instant" })
    input.focus({ preventScroll: true })
  }

  /** @returns {void} Reserve the actual height, including attachment confirmation. */
  resizeFooter() {
    const left = this.element.closest(".page-container")?.getBoundingClientRect().left || 0
    this.element.style.setProperty("--response-footer-left", `${Math.max(0, left)}px`)
    this.element.style.setProperty("--response-footer-height", `${this.footerTarget.getBoundingClientRect().height}px`)
  }

  /** @returns {void} Report progress without implying the server has saved. */
  submitting() { this.setText(this.saveStatusTarget, "Saving response…") }

  /**
   * @param {CustomEvent} event Turbo submission result.
   * @returns {void} Report failed requests; successful saves are rendered by the server.
   */
  submissionFinished(event) { if (!event.detail.success) this.submissionFailed() }

  /** @returns {void} Keep failed uploads and submissions visibly unsaved. */
  submissionFailed() {
    this.setText(this.saveStatusTarget, "Response not saved. Please try again.")
  }

  /**
   * Kept in step with TerritoryCheck#recording_manual_answer_needing_detail?. A "no" that leaves
   * nearby areas open is actionable only with a reason or somewhere else to look, and a claim that
   * nearby areas are available is worth nothing until one is named. A "no" with nearby ruled out,
   * and anything else, answers itself.
   * @param {HTMLElement} card Requested location card.
   * @returns {boolean} Whether this answer owes the advisor a reason or an alternative.
   */
  cardNeedsDetail(card) {
    const nearby = card.querySelector(".alternate-nearby--js")?.value
    if (nearby === "true") return true

    return UNAVAILABLE_RESULTS.includes(card.querySelector(".alternate-availability--js")?.value) &&
      nearby !== "false"
  }

  /**
   * @param {HTMLElement} card Requested location card.
   * @returns {boolean} Whether an answer owing detail carries an explanation or an alternative.
   */
  cardValid(card) {
    if (!this.cardNeedsDetail(card)) return true

    const notes = card.querySelector(".alternate-notes--js")?.value || ""
    if (notes.replace(/\s+/g, "").length >= 2) return true

    return Array.from(card.querySelectorAll(".nearby-entry-js")).some((entry) => {
      if (entry.querySelector(".nearby-entry-destroy-js")?.value === "true") return false
      if (entry.querySelector(".place-autocomplete-js")?.dataset.placeVerified !== "true") return false

      return Boolean(entry.querySelector(".nearby-entry-city-id-js")?.value ||
        entry.querySelector(".nearby-entry-zip-code-id-js")?.value)
    })
  }

  /**
   * @param {MouseEvent} event Add or remove a nearby territory.
   * @returns {void} Update entries and response feedback.
   */
  handleClick(event) {
    const addButton = event.target.closest(".nearby-entry-add-js")
    if (addButton) {
      event.preventDefault()
      const section = addButton.closest(".nearby-section-js")
      const template = section.querySelector(".nearby-entry-template-js")
      const html = template.innerHTML.replace(/NEW_RECORD/g, String(this.nextEntryIndex++))
      section.querySelector(".nearby-entries-js").insertAdjacentHTML("beforeend", html)
      this.refresh()
      section.querySelector(".nearby-entry-js:last-child .place-autocomplete-js")?.focus()
      return
    }

    const removeButton = event.target.closest(".nearby-entry-remove-js")
    if (!removeButton) return

    event.preventDefault()
    const entry = removeButton.closest(".nearby-entry-js")
    const savedId = entry.querySelector('input[name$="[id]"]')?.value
    if (savedId) {
      entry.querySelector(".nearby-entry-destroy-js").value = "true"
      entry.querySelector(".place-autocomplete-js").disabled = true
      entry.hidden = true
    } else {
      entry.remove()
    }
    this.refresh()
  }
}
